import { useChatStore } from '~/stores/chat'
import { getWebSocketManager, startWebSocketStatusWatcher } from '~/utils/websocket'
import { useUserStore } from '~/stores/user'
import { nextTick } from 'vue'

// WebSocket 初始化函数
// 防重复初始化（HMR/路由重装等场景）
let __wsInited = false
try {
  if (typeof window !== 'undefined') {
    __wsInited = !!(window as any).__wsInited
  }
} catch (_) {}

// 🔧 保存定时器 ID，用于清理
let healthCheckTimer: NodeJS.Timeout | null = null
let visibilityCheckTimer: NodeJS.Timeout | null = null

// 🔧 清理所有定时器
export function cleanupWebSocketTimers() {
  if (healthCheckTimer) {
    clearInterval(healthCheckTimer)
    healthCheckTimer = null
    console.log('✅ 已清理健康检查定时器')
  }
  if (visibilityCheckTimer) {
    clearInterval(visibilityCheckTimer)
    visibilityCheckTimer = null
    console.log('✅ 已清理可见性检查定时器')
  }
}

export function initWebSocket() {
  if (__wsInited) {
    console.log('⚠️ WebSocket 已初始化，跳过重复初始化')
    return
  }
  
  // 🔧 先清理旧的定时器（防止HMR导致的重复）
  cleanupWebSocketTimers()
  
  __wsInited = true
  try {
    if (typeof window !== 'undefined') {
      ;(window as any).__wsInited = true
    }
  } catch (_) {}

  const chatStore = useChatStore()
  const wsManager = getWebSocketManager()
  const useStore = useUserStore()

  // 设置WebSocket事件监听器（仅注册一次）
  wsManager.on('open', () => {
    chatStore.wsConnected = true
    chatStore.wsConnecting = false
    chatStore.wsError = null

    // 🔧 启动30秒自动重连定时器
    chatStore.startAutoReconnectTimer()
  })

  wsManager.on('message', (raw: any) => {
    // 兼容服务端将 payload 放在 data 字段且为字符串的情况
    let data: any = raw
    try {
      if (typeof raw === 'string') {
        data = JSON.parse(raw)
      }
    } catch {}

    try {
      if (data && typeof data.data === 'string') {
        data.data = JSON.parse(data.data)
      }
    } catch {}
    if (data?.data?.balance) {
      useStore.setMsgCost(data.data.balance)
    } else if (data?.data?.balance === 0) {
      useStore.setMsgCost(0)
    }
    chatStore.handleWebSocketMessage(data)
  })

  wsManager.on('error', () => {
    chatStore.wsError = 'WebSocket连接失败'
    chatStore.wsConnected = false
    chatStore.wsConnecting = false

    // 🔧 停止30秒自动重连定时器
    chatStore.stopAutoReconnectTimer()

    // 🔧 优化：使用统一的断开处理方法
    chatStore.handleWebSocketDisconnect()
  })

  wsManager.on('close', () => {
    chatStore.wsConnected = false
    chatStore.wsConnecting = false

    // 🔧 停止30秒自动重连定时器
    chatStore.stopAutoReconnectTimer()

    // 🔧 优化：使用统一的断开处理方法
    chatStore.handleWebSocketDisconnect()
    chatStore.clearAllDigitalHumanStates({ loading: false, typing: false })
  })

  // 在客户端启动时建立WebSocket连接
  nextTick(async () => {
    const status = wsManager.getStatus()
    if (!status.isConnected && !status.isConnecting) {
      chatStore.wsConnecting = true
      wsManager.connect()
    }

    // 🔧 iOS 内嵌 H5 优化：监听多种场景的网络恢复和前后台切换
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const ensureConnected = () => {
        const s = wsManager.getStatus()

        // 检查连接状态，如果断开则重连
        if (!s.isConnected && !s.isConnecting) {
          console.log('🔄 [iOS优化] 检测到未连接，尝试重连 WebSocket')
          chatStore.wsConnecting = true
          wsManager.connect()
        } else if (s.isConnected) {
          // 即使显示已连接，也检查实际 WebSocket 状态
          // iOS 后台恢复后，WebSocket 可能已经断开但状态未更新
          if (!wsManager.isReallyConnected()) {
            console.log('🔄 [iOS优化] 状态不一致，实际已断开，尝试重连')
            chatStore.wsConnecting = true
            wsManager.connect()
          } else {
            console.log('✅ [iOS优化] WebSocket 连接正常')
          }
        }
      }

      // 1. 监听页面可见性变化（标准方式）
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          console.log('👁️ [iOS优化] 页面变为可见，检查连接')
          ensureConnected()
        } else {
          console.log('🙈 [iOS优化] 页面变为隐藏')
        }
      })

      // 2. 监听窗口焦点（iOS Safari 兼容）
      window.addEventListener('focus', () => {
        console.log('🎯 [iOS优化] 窗口获得焦点，检查连接')
        ensureConnected()
      })

      // 3. 监听 pageshow 事件（iOS 特有，从 bfcache 恢复时触发）
      window.addEventListener('pageshow', event => {
        if (event.persisted) {
          console.log('📄 [iOS优化] 从 bfcache 恢复，检查连接')
          ensureConnected()
        }
      })

      // 4. 监听 pagehide 事件（iOS 进入后台）
      window.addEventListener('pagehide', () => {
        console.log('📴 [iOS优化] 页面即将隐藏/卸载')
        // 不主动断开，让心跳机制处理
      })

      // 5. 监听网络状态变化
      if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
        window.addEventListener('online', () => {
          console.log('🌐 [iOS优化] 网络已恢复')
          setTimeout(() => {
            ensureConnected()
          }, 500) // 延迟500ms，等待网络完全恢复
        })

        window.addEventListener('offline', () => {
          console.warn('🌐 [iOS优化] 网络已断开，清理 loading 状态')
          chatStore.clearAllDigitalHumanStates({
            loading: false,
            typing: false
          })
        })
      }

      // 6. 监听网络类型变化（WiFi/4G 切换）
      if ('connection' in navigator) {
        const connection = (navigator as any).connection
        if (connection) {
          connection.addEventListener('change', () => {
            console.log(`📶 [iOS优化] 网络类型变化: ${connection.effectiveType}`)
            // 网络切换后延迟检查连接
            setTimeout(() => {
              ensureConnected()
            }, 1000)
          })
        }
      }

      // 7. 定期健康检查（iOS 弱网环境下的保底措施）
      // 🔧 清理旧的定时器（如果存在）
      if (healthCheckTimer) {
        clearInterval(healthCheckTimer)
      }
      
      healthCheckTimer = setInterval(() => {
        const s = wsManager.getStatus()
        if (document.visibilityState === 'visible' && !s.isConnecting) {
          if (!s.isConnected || !wsManager.isReallyConnected()) {
            console.log('🏥 [iOS优化] 定期健康检查发现断开，尝试重连')
            ensureConnected()
          }
        }
      }, 15000) // 每15秒检查一次
      
      console.log('✅ 启动健康检查定时器（每15秒）')
    }

    // 🔧 新增：WebSocket连接建立后检查状态恢复
    wsManager.on('open', async () => {
      try {
        const recovered = await chatStore.checkAndRecoverState()
        if (recovered) {
          // 成功恢复页面刷新前的状态
        } else {
          // 无需恢复状态
        }

        // 🔧 清除所有数字人的过期loading状态
        chatStore.clearAllDigitalHumanStates({
          loading: false,
          typing: false
        })

        // 🔧 WebSocket重连后补全断网期间丢失的消息
        // 注意：这里只处理WebSocket级别重连，浏览器级别网络恢复由naturalCapacity.ts处理
        setTimeout(async () => {
          try {
            console.log('🔄 WebSocket重连，检查断网期间丢失的消息...')
            await chatStore.recoverMissingAIReplies()
          } catch (error) {
            console.warn('WebSocket重连消息补全失败:', error)
          }
        }, 1500) // 比浏览器网络恢复稍早，确保WebSocket先恢复
      } catch (error) {
        console.error('状态恢复失败:', error)
      }
    })

    // 🔧 新增：启动全局 WebSocket 连接状态监听器
    // 每 5 秒检查一次连接状态，如果未连接则自动重连
    startWebSocketStatusWatcher()
    
    // 🔧 新增：页面卸载时清理定时器
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        cleanupWebSocketTimers()
        console.log('🧹 页面卸载，清理所有定时器')
      })
    }
  })

  return wsManager
}
